import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { from } from "rxjs";
import {
    filter,
    switchMap,
    tap,
    withLatestFrom,
    debounceTime,
    map,
} from "rxjs/operators";

import {
    catchSdkError,
    checkTimeOut,
    fetchShadeFinderConfiguration,
    fetchShadeFinderConfigurationSuccess,
    launchCamera,
} from "../actions/shade-finder.actions";

import { ShadeFinderState } from "../reducers/shade-finder-state";
import { selectModalStatusState } from "../selectors/shade-finder.selectors";
import { ShadeFinderService } from "../services/shade-finder.service";
import { ShadeFinderSdkError } from "../types/shade-finder.types";

const TIMEOUT_ERROR_MS = 40_000;

@Injectable()
export class ShadeFinderEffects {
    public fetchShadeFinderConfiguration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fetchShadeFinderConfiguration),
            switchMap(() =>
                this.shadeFinderService.getShadeFinderConfiguration().pipe(
                    switchMap((shadeFinderConfig) => [
                        fetchShadeFinderConfigurationSuccess({
                            shadeFinderConfig,
                        }),
                    ]),
                ),
            ),
        ),
    );

    public launchTimeoutCamera$ = createEffect(() =>
        this.actions$.pipe(
            ofType(launchCamera),
            switchMap(() =>
                from(
                    navigator.mediaDevices.getUserMedia({
                        video: true,
                    }),
                ),
            ),
            tap((stream: MediaStream) =>
                stream.getTracks().forEach((track) => track.stop()),
            ),
            // start timeout after user authorized video
            debounceTime(TIMEOUT_ERROR_MS),
            map(() => checkTimeOut()),
        ),
    );

    public checkTimeOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(checkTimeOut),
            withLatestFrom(this.store.select(selectModalStatusState)),
            filter(
                ([, modalStatusState]) =>
                    modalStatusState !== "CAMERA_LOADED" &&
                    modalStatusState !== "ON_ERROR" &&
                    modalStatusState !== "NONE",
            ),
            map(() =>
                catchSdkError({ sdkError: ShadeFinderSdkError.ERROR_TIMEOUT }),
            ),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store<ShadeFinderState>,
        private shadeFinderService: ShadeFinderService,
    ) {}
}
